import { Alert, Form, Modal, Select } from 'antd';
import { FC, memo, useCallback, useEffect, useState } from 'react';
import { fetchDetachImpact } from 'services/api/organizations';
import { fetchUsers } from 'services/api/users';
import { USER_ROLES } from 'constants/users';
import { UserListRow } from 'types/api/users';

import { showErrorMessage } from 'utils/showErrorMessage';

interface IProps {
  admin: UserListRow;
  organization: { id: number; name: Record<string, string> };
  onClose(): void;
  /** Is this admin on the standing ALL grant, which any detach ends? */
  endsAllOrganizations: boolean;
  /** Stage the removal. `reassignTo` is who inherits, when anyone must. */
  onConfirm(reassignTo?: number): void;
}

/**
 * Remove an admin from an organization — and hand over what they own.
 *
 * DETACHING IS NOT A DELETE. POST /users records the creator in
 * core.admins.parent_id, and visibleAdminIds reads that column to decide
 * who appears in the admins list at all. So removing someone who created
 * other admins can leave those accounts inside the organization with
 * nobody able to see or manage them — the kind of loss that is silent
 * until somebody goes looking for a colleague who is no longer listed.
 *
 * The backend answers the question before we ask the operator anything:
 * detach-impact names who would be stranded, and the detach itself
 * refuses with a 400 if they would be stranded and no successor was
 * given. The reassignment and the removal commit together, which is why
 * this sends a successor rather than reassigning afterwards.
 *
 * So this dialog never asks "are you sure" over an empty question. With
 * nothing to hand over it is a plain confirmation; with something, the
 * operator must say who inherits it before they can continue.
 *
 * NOTHING IS WRITTEN HERE. detach-impact is a read; the removal itself is
 * staged and sent when the edit dialog is saved, with the successor chosen
 * here carried along. Until then Cancel on either dialog leaves the
 * organization exactly as it was.
 */
const DetachOrganization: FC<IProps> = ({
  admin,
  organization,
  endsAllOrganizations,
  onClose,
  onConfirm
}) => {
  const [form] = Form.useForm();
  const [impact, setImpact] = useState<{
    orphans: number[];
    requiresSuccessor: boolean;
  } | null>(null);

  const [candidates, setCandidates] = useState<UserListRow[]>([]);

  const orgName = organization.name?.en ?? String(organization.id);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const result = await fetchDetachImpact(organization.id, admin.id);
        if (cancelled) return;

        setImpact(result);

        if (!result.requiresSuccessor) return;

        // Who may inherit: an admin of THIS organization, which is what
        // the backend will check. There is no endpoint listing an
        // organization's admins, but every row of the admins list now
        // carries its own memberships, so the list answers it. One page
        // large enough to hold them all — the scope is still whatever
        // this operator may see, which is the right ceiling for a picker.
        const { results } = await fetchUsers({
          page: 1,
          limit: 500,
          role: USER_ROLES.admin
        });

        if (cancelled) return;

        setCandidates(
          results.filter(
            row =>
              row.id !== admin.id &&
              (row.organizations ?? []).some(org => org.id === organization.id)
          )
        );
      } catch (err) {
        if (!cancelled) showErrorMessage(err);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [admin.id, organization.id]);

  const onFinish = useCallback(
    (values: { reassignTo?: number }) => {
      onConfirm(values.reassignTo);
    },
    [onConfirm]
  );

  const orphans = impact?.orphans.length ?? 0;

  return (
    <Modal
      open
      title="Remove from organization"
      onCancel={onClose}
      width={600}
      onOk={form.submit}
      okText="Remove"
      okButtonProps={{ danger: true, disabled: !impact }}
      destroyOnClose
    >
      <Alert
        showIcon
        type={orphans ? 'warning' : 'info'}
        style={{ marginBottom: 16 }}
        message={
          orphans
            ? `${orphans} account(s) here were created by this admin`
            : `Remove ${admin.first_name} ${admin.last_name} from ${orgName}?`
        }
        description={
          orphans
            ? 'They are the only link to those accounts: the admins list decides who is visible from who created whom, so removing this admin leaves them in the organization with nobody able to see them. Choose who inherits them — the handover happens in the same step as the removal, when you save.'
            : 'Nothing else is attached to them here, so nothing needs handing over. It takes effect when you save.'
        }
      />
      {/*
        An admin missing one organization is a list, not "all", so the
        server clears the standing grant along with the membership. What
        the operator loses is the part that is not on screen: every
        organization created from now on. Saying it after the fact would be
        telling them about a decision they did not know they were making.
      */}
      {endsAllOrganizations && (
        <Alert
          showIcon
          type="warning"
          style={{ marginBottom: 16 }}
          message="This ends their All organizations grant"
          description="They keep the rest as a fixed list, and organizations created from now on will no longer be added for them. Granting All again is a superadmin action."
        />
      )}
      <Form form={form} onFinish={onFinish} layout="vertical" size="large">
        {!!orphans && (
          <Form.Item
            name="reassignTo"
            label="Hand those accounts to"
            rules={[{ required: true }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Select an admin"
              options={candidates.map(row => ({
                value: row.id,
                label: `${row.first_name} ${row.last_name} (${row.email})`
              }))}
            />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

export default memo(DetachOrganization);
